import { type UploaderFile } from './Uploader';

export enum FileType {
  // Images
  IMAGE = 'image',
  JPEG = 'jpeg',
  PNG = 'png',
  GIF = 'gif',
  WEBP = 'webp',
  SVG = 'svg',
  ICO = 'ico',

  // Documents
  PDF = 'pdf',
  DOC = 'doc',
  DOCX = 'docx',
  XLS = 'xls',
  XLSX = 'xlsx',
  PPT = 'ppt',
  PPTX = 'pptx',
  TXT = 'txt',
  CSV = 'csv',

  // Audio
  AUDIO = 'audio',
  MP3 = 'mp3',
  WAV = 'wav',
  OGG = 'ogg',

  // Video
  VIDEO = 'video',
  MP4 = 'mp4',
  WEBM = 'webm',
  AVI = 'avi',
  MOV = 'mov',

  // Archives
  ZIP = 'zip',
  RAR = 'rar',
  TAR = 'tar',
  GZ = 'gz',

  // Code
  JSON = 'json',
  XML = 'xml',
  HTML = 'html',
  CSS = 'css',
  JS = 'js',
}

// cspell:disable
const FILE_TYPE_MAP: Record<FileType, string> = {
  // Images
  [FileType.IMAGE]: 'image/*',
  [FileType.JPEG]: 'image/jpeg',
  [FileType.PNG]: 'image/png',
  [FileType.GIF]: 'image/gif',
  [FileType.WEBP]: 'image/webp',
  [FileType.SVG]: 'image/svg+xml',
  [FileType.ICO]: 'image/x-icon',

  // Documents
  [FileType.PDF]: 'application/pdf',
  [FileType.DOC]: 'application/msword',
  [FileType.DOCX]:
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  [FileType.XLS]: 'application/vnd.ms-excel',
  [FileType.XLSX]:
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  [FileType.PPT]: 'application/vnd.ms-powerpoint',
  [FileType.PPTX]:
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  [FileType.TXT]: 'text/plain',
  [FileType.CSV]: 'text/csv',

  // Audio
  [FileType.AUDIO]: 'audio/*',
  [FileType.MP3]: 'audio/mpeg',
  [FileType.WAV]: 'audio/wav',
  [FileType.OGG]: 'audio/ogg',

  // Video
  [FileType.VIDEO]: 'video/*',
  [FileType.MP4]: 'video/mp4',
  [FileType.WEBM]: 'video/webm',
  [FileType.AVI]: 'video/x-msvideo',
  [FileType.MOV]: 'video/quicktime',

  // Archives
  [FileType.ZIP]: 'application/zip',
  [FileType.RAR]: 'application/vnd.rar',
  [FileType.TAR]: 'application/x-tar',
  [FileType.GZ]: 'application/gzip',

  // Code
  [FileType.JSON]: 'application/json',
  [FileType.XML]: 'application/xml',
  [FileType.HTML]: 'text/html',
  [FileType.CSS]: 'text/css',
  [FileType.JS]: 'text/javascript',
};
// cspell:enable

export const fileTypesToAccept = (types: FileType[]): string => {
  return types.map(type => FILE_TYPE_MAP[type]).join(',');
};

export const getAcceptString = (accept: FileType[]): string => {
  return fileTypesToAccept(accept);
};

// Default accepted file types: DOC, DOCX, XLS, XLSX, PPT, PPTX, CSV, PDF, PNG, JPG/JPEG
export const DEFAULT_ACCEPTED_FILE_TYPES: FileType[] = [
  FileType.DOC,
  FileType.DOCX,
  FileType.XLS,
  FileType.XLSX,
  FileType.PPT,
  FileType.PPTX,
  FileType.CSV,
  FileType.PDF,
  FileType.PNG,
  FileType.JPEG,
];

// Default max file size: 20MB
export const DEFAULT_MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB in bytes

export type BaseUploadFileItem = {
  fileName: string;
  fileSize: number;
  fileType: string;
  fileKey: string;
  error?: string;
  lastModified?: number;
  uuid?: string;
};

export type BaseUploadingFileItem = BaseUploadFileItem & {
  progress: number;
};

export const convertFile = (file: UploaderFile): BaseUploadFileItem => {
  return {
    fileName: file.name,
    fileSize: file.size,
    fileType: file.type,
    fileKey: file.fileKey ?? '',
    ...file,
    lastModified: file.lastModified,
  };
};

export const validateFile = (
  file: File,
  options?: {
    maxSize?: number;
    accept?: string;
  },
): string | undefined => {
  const { accept, maxSize } = options ?? {};

  // Check file size
  if (maxSize && file.size > maxSize) {
    const maxSizeMB = Math.round(maxSize / 1024 / 1024);
    return `The ${file.name} file exceeds the maximum size of ${formatFileSize(maxSize)}. Please upload a file smaller than ${maxSizeMB}MB.`;
  }

  // Check accept types
  if (accept) {
    const acceptedTypes = accept.split(',').map((t: string) => t.trim());
    const fileType = file.type;
    const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;

    const isAccepted = acceptedTypes.some((type: string) => {
      if (type.startsWith('.')) {
        return fileExtension === type.toLowerCase();
      }
      if (type.endsWith('/*')) {
        return fileType.startsWith(type.replace('/*', '/'));
      }
      return fileType === type;
    });

    if (!isAccepted) {
      return `This ${file.name} file type is not supported`;
    }
  }

  return undefined;
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) {
    return '0 Bytes';
  }
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};
